import { useCallback, useEffect, useState } from 'react';

/**
 * Where the intake form keeps its draft. Exported so signing out can remove it:
 * the draft holds a complainant's contact details, and the next person to sign
 * in on the same tab must not be offered them back.
 */
export const INTAKE_DRAFT_KEY = 'gov.intake.draft';

/**
 * The draft key for one intake source. A manual intake, an intake accepting a
 * citizen report and one opened from a front-desk entry are different pieces
 * of work: under one shared key, a half-typed manual intake was restored over
 * a citizen report's own words (its description replaced) the next time the
 * officer accepted a report.
 */
export function intakeDraftKey(source: {
  complaintId?: string | null;
  contactLogId?: string | null;
}): string {
  if (source.complaintId) return `${INTAKE_DRAFT_KEY}:complaint:${source.complaintId}`;
  if (source.contactLogId) return `${INTAKE_DRAFT_KEY}:contact:${source.contactLogId}`;
  return INTAKE_DRAFT_KEY;
}

/** Remove every intake draft, whatever its source — on sign-out. */
export function clearIntakeDrafts(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key === INTAKE_DRAFT_KEY || key.startsWith(`${INTAKE_DRAFT_KEY}:`)))
        doomed.push(key);
    }
    doomed.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage unavailable — there is nothing stored to remove.
  }
}

/**
 * Keeps an in-progress form alive across a page reload.
 *
 * Intake is the longest form in the product and it has no server-side draft —
 * the file does not exist until submit — so until now a refresh, a misfired
 * browser gesture or a crashed tab threw away everything the officer had typed,
 * including details taken down over the phone that they may have no way to
 * recover.
 *
 * **sessionStorage, deliberately, not localStorage.** A complaint carries the
 * complainant's name, phone number and address, and the owner's. These are
 * shared municipal workstations; localStorage would leave that on the machine
 * indefinitely, readable by whoever sits down next. sessionStorage is scoped to
 * the tab and cleared when it closes, which covers the reload this exists to fix
 * and nothing beyond it.
 *
 * Restoring is reported rather than silent: `restored` lets the caller say so,
 * because a form that fills itself in without explanation is worse than an empty
 * one.
 */
export interface SessionDraft<T> {
  /** What was recovered on mount, or null if there was nothing to recover. */
  restored: T | null;
  /** Persist the current value. Safe to call on every change. */
  save: (value: T) => void;
  /** Drop the stored draft — on submit, or when the officer discards. */
  clear: () => void;
}

/** Reads never throw: a corrupt or unreadable entry means "no draft", not a crash. */
function read<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function useSessionDraft<T>(key: string): SessionDraft<T> {
  // Read once, on mount. Reading on every render would resurrect a draft the
  // officer had just cleared.
  const [restored] = useState<T | null>(() => read<T>(key));

  const save = useCallback(
    (value: T) => {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Full quota or a browser with storage disabled. Losing the safety net
        // is not a reason to interrupt the officer mid-form.
      }
    },
    [key],
  );

  /**
   * Deliberately does not latch: after the officer starts over, whatever they
   * type next is new work and deserves the same protection.
   */
  const clear = useCallback(() => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Nothing to do — see `save`.
    }
  }, [key]);

  return { restored, save, clear };
}

/**
 * Persist `value` for as long as the component is mounted.
 *
 * Split from the hook above so the caller can assemble the snapshot from several
 * pieces of state without having to remember to save after each one.
 */
export function useSessionDraftSync<T>(draft: SessionDraft<T>, value: T): void {
  const { save } = draft;
  useEffect(() => {
    save(value);
  }, [save, value]);
}
